import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /**
   * Plan v0.3.0 §G-1 (PPTX Slide 7+8) — when `true` (the legacy
   * behavior), clicking the dim backdrop dismisses the modal. When
   * `false` (the new default), backdrop clicks are ignored and only
   * Esc + the X button close the modal. Operators noted that
   * accidental backdrop clicks while typing into long forms (new
   * customer / new robot) wiped 30+ seconds of input. Esc + X stay
   * functional in both modes; this only affects the backdrop.
   */
  dismissOnBackdrop?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 480,
  dismissOnBackdrop = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Keep the latest onClose in a ref so the open/focus effect below can depend
  // ONLY on `open`. Parents pass an inline arrow as onClose (new identity every
  // render), so including onClose in the deps re-ran this effect on every
  // keystroke — the requestAnimationFrame then stole focus to the first
  // focusable (the × close button), bouncing the caret out of the field the
  // user was typing into. Reading onClose via the ref decouples the two.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Focus the first form control on open — prefer inputs/selects over the ×
    // close button so opening a form modal lands the caret in the first field,
    // not on the dismiss button. Falls back to any focusable for a11y so focus
    // always enters the dialog.
    window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target =
        panel.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? panel.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    });

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      // Length-checked above, so both are present — narrow for
      // noUncheckedIndexedAccess.
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
    // Depends only on `open` — onClose is read via onCloseRef so a new inline
    // onClose identity from the parent does not re-run this effect (which would
    // steal focus on every keystroke). See onCloseRef note above.
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      // Plan §G-1: only dismiss on backdrop click when explicitly opted in.
      onClick={dismissOnBackdrop ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // FB2 §8 (결정 I) — Drawer 오버레이(zIndex 60) 위로 올려 드로어 내부에서
        // 연 모달이 드로어 뒤에 깔리지 않게 한다. Toast/NotificationBell(99/100)
        // 보다는 낮아 토스트·알림 패널은 종전대로 모달 위에 표시된다.
        zIndex: 70,
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={{
          width,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100vh - 48px)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 20px',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div id="modal-title" style={{ fontSize: 15, fontWeight: 600 }}>
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'transparent',
              fontSize: 18,
              cursor: 'pointer',
              color: 'var(--fg-muted)',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </header>
        <div style={{ flex: 1, padding: 20, overflow: 'auto' }}>{children}</div>
        {footer && (
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '12px 20px',
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
